import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { getGlobalStyleSheets } from '../../global-styles';
import { AtlIconTemplate } from '../icon/icon.component';
import { IconEnums } from '../icon';
import { enumAttributeConverter } from '../enum-types';

import SearchInputStyles from './search-input.scss?inline';

import { SearchTags, AtlSearchEvent } from './search.types.ts';

@customElement(SearchTags.SearchInput)
export class AtlSearchInput extends LitElement {

    static styles = [
        ...getGlobalStyleSheets(),
        SearchInputStyles
    ];

    @property({ type: String })
    placeholder = 'Search...';

    @property({ attribute: 'disable-auto-search', type: Boolean })
    disableAutoSearch = false;

    @property({ attribute: 'button-aria-label', type: String })
    buttonAriaLabel = 'Search';

    @property({ attribute: 'input-aria-label', type: String })
    inputAriaLabel = 'Search';

    @property({ attribute: 'debounce-ms', type: Number })
    debounceMs = 2000;

    @property({ attribute: 'icon-source', converter: enumAttributeConverter(IconEnums.IconSource) })
    iconSource: IconEnums.IconSource = IconEnums.IconSource.FontAwesome;

    @property({ attribute: 'icon-name', type: String })
    iconName = 'magnifying-glass';

    @state()
    private _value = '';

    private _debounceTimer: ReturnType<typeof setTimeout> | undefined;

    disconnectedCallback(): void {
        super.disconnectedCallback();
        clearTimeout(this._debounceTimer);
    }

    private _handleInput(e: Event): void {
        this._value = (e.target as HTMLInputElement).value;        
        clearTimeout(this._debounceTimer);
        if (this.disableAutoSearch) return;
        this._debounceTimer = setTimeout(() => this._dispatchSearch(), this.debounceMs);
    }

    private _handleKeyDown(e: KeyboardEvent): void {
        if (e.key === 'Enter') {
            clearTimeout(this._debounceTimer);
            this._dispatchSearch();
        }
    }

    private _handleSearchClick(): void {
        clearTimeout(this._debounceTimer);
        this._dispatchSearch();
    }

    private _dispatchSearch(): void {
        this.dispatchEvent(new AtlSearchEvent(this._value));
    }

    render() {
        return html`
            <div class="input-group">
                <input
                    type="text"
                    class="form-control border-end-0"
                    placeholder=${this.placeholder}
                    aria-label=${this.inputAriaLabel}
                    .value=${this._value}
                    @input=${this._handleInput}
                    @keydown=${this._handleKeyDown}
                />
                <button
                    type="button"
                    class="input-group-text clickable search-button"
                    aria-label=${this.buttonAriaLabel}
                    @click=${this._handleSearchClick}
                >
                    ${AtlIconTemplate({ iconSource: this.iconSource, iconName: this.iconName, ariaHidden: true })}
                </button>
            </div>`;
    }
}